import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Plus } from "lucide-react";
import { useState } from "react";
import { api } from "../../lib/api";
import { MODULE_TOGGLES, type Company, type ModuleToggleKey } from "./shared";

export function CompaniesTable({
  companies,
  onOpen,
}: {
  companies: Company[];
  onOpen(id: string): void;
}) {
  return (
    <div className="ops-table-frame border border-border rounded-md overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs text-muted border-b border-border">
          <tr>
            <th className="text-left p-2">Company</th>
            <th className="text-left p-2">Slug</th>
            <th className="text-left p-2">Status</th>
            <th className="text-left p-2">Modules</th>
            <th className="text-right p-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {companies.map((c) => (
            <tr key={c.id} className="border-b border-border/60 last:border-0">
              <td className="p-2">{c.name}</td>
              <td className="p-2 text-muted">{c.slug}</td>
              <td className="p-2">{c.disabledAt ? "Disabled" : "Active"}</td>
              <td className="p-2">
                <div className="flex flex-wrap gap-1">
                  {MODULE_TOGGLES.map((m) => (
                    <CompanyModuleToggle
                      key={m.key}
                      company={c}
                      moduleKey={m.key}
                      label={m.label}
                    />
                  ))}
                </div>
              </td>
              <td className="p-2 text-right">
                <button
                  type="button"
                  onClick={() => onOpen(c.id)}
                  className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:bg-muted/10"
                >
                  <Check size={14} />
                  Open
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CompanyModuleToggle({
  company,
  moduleKey,
  label,
}: {
  company: Company;
  moduleKey: ModuleToggleKey;
  label: string;
}) {
  const queryClient = useQueryClient();
  const on = company[moduleKey];
  const mutation = useMutation({
    mutationFn: () =>
      api(`/admin/companies/${company.id}`, {
        method: "PATCH",
        body: JSON.stringify({ [moduleKey]: !on }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "companies"] });
      void queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });
  return (
    <button
      type="button"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-xs disabled:opacity-50 ${
        on ? "border-posGreen/50 text-posGreen" : "border-border text-muted"
      }`}
    >
      {label} {on ? "on" : "off"}
    </button>
  );
}

export function CreateCompanyForm() {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      api("/admin/companies", {
        method: "POST",
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => {
      setName("");
      void queryClient.invalidateQueries({ queryKey: ["admin", "companies"] });
    },
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
      className="ops-panel ops-admin-create-form flex flex-wrap gap-2"
    >
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Company name"
        className="min-w-64 bg-transparent border border-border rounded px-2 py-1.5 text-sm"
        required
      />
      <button
        type="submit"
        className="ops-primary inline-flex items-center gap-1 rounded bg-accent text-white px-3 py-1.5 text-sm disabled:opacity-50"
        disabled={mutation.isPending}
      >
        <Plus size={14} />
        Create company
      </button>
    </form>
  );
}
